import {
	Button,
	ButtonGroup,
	Card,
	Icon,
	IconName,
	Popover,
	Section,
	SectionCard,
	Switch,
} from '@snpjs/core';
import { useEffect, useRef, useState } from 'react';

import { ABAPBoolean } from '../../../shared/types/ABAPBoolean';
import { Color } from '../../../api/types/Color';
import { CompoundTags } from '../../../components/selection/compoundTags/CompoundTags';
import { IMessage } from '../../../model/message/IMessage';
import { ISystemLandscapeHeaderProps } from './ISystemLandscapeHeaderProps';
import { Link } from 'react-router-dom';
import { MessageArea } from '../../../components/messageArea/MessageArea';
import { MessageSeverity } from '../../../model/message/MessageSeverity';
import { formatDate } from '../../../utils/formatDate';
import styles from './SystemLandscapeHeader.module.scss';
import { texts } from '../../../i18n/texts';
import { useAuth } from '../../../hooks/useAuth';
import { useConfig } from '../../../hooks/useConfig';
import { useProductInfo } from '../../../hooks/useProductInfo';
import { useSetAllLicensesCommand } from '../systemCommands/useSetAllLicensesCommand';
import useTranslation from '../../../hooks/useTranslation';
import { useUserProfile } from '../../../hooks/useUserProfile';

export const SystemLandscapeHeader: React.FC<ISystemLandscapeHeaderProps> = (
	props
) => {
	const { t } = useTranslation();
	const config = useConfig();
	const productInfo = useProductInfo();
	const [disableToggle, setDisableToggle] = useState(false);
	const [allLicensesToggle, setAllLicensesToggle] = useState(
		props.product.HAS_FULL_LICENSE === ABAPBoolean.true ? true : false
	);
	const [message, setMessage] = useState<IMessage | undefined>(undefined);
	const [isHoveringClipboardButton, setHoveringClipboardButton] =
		useState(false);
	const [isHoveringClipboardMenu, setHoveringClipboardMenu] = useState(false);
	const [isClipboardPopoverOpen, setClipboardPopoverOpen] = useState(false);
	const reason: string = '';
	let reasonRef = useRef(reason);
	const userProfile = useUserProfile();
	const onToggleLicenses = async () => {
		if (disableToggle) {
			return;
		}
		setDisableToggle(true);
		setAllLicensesToggle(!allLicensesToggle);
		// updating the state ist asynchronous, meaning that props.onSetAllLicensesFlag() will not receive the changed value => initial negative value is sent
		const updatedValue = await props.onSetAllLicensesFlag(
			!allLicensesToggle,
			reasonRef.current
		);
		setAllLicensesToggle(updatedValue);
		setDisableToggle(false);
		return updatedValue === !allLicensesToggle;
	};

	useEffect(() => {
		setClipboardPopoverOpen(
			isHoveringClipboardButton || isHoveringClipboardMenu
		);
	}, [isHoveringClipboardButton, isHoveringClipboardMenu]);

	const collapseLicenseOptions = !(
		userProfile.value === undefined ||
		userProfile.value.collapseLicenseOptions === undefined ||
		userProfile.value.collapseLicenseOptions.length === 0 ||
		(() => {
			const collapseLicenseEntry =
				userProfile.value.collapseLicenseOptions.find(
					([id, flag]) => id === props.product.OBJECT_ID
				);
			if (collapseLicenseEntry === undefined) {
				return true;
			}
			return collapseLicenseEntry[1];
		})()
	);

	const toggleAllLicensesCommand = useSetAllLicensesCommand({
		onOkay: onToggleLicenses,
		reasonRef: reasonRef,
		productId: props.product.PRODUCT_ID,
		allLicensesToggle: allLicensesToggle,
	});

	useEffect(() => {
		if (!productInfo.isLicenseActive(props.product)) {
			setMessage(() => ({
				severity: 'error',
				text: t(texts.systemCard.errorProductExpired),
			}));
		} else if (
			!productInfo.isAnyLicenseLongLasting(
				props.product,
				config.CONTRACT_EXPIRE_WARN_IN_DAYS
			)
		) {
			let endDate = new Date();
			if (productInfo.getEmergencyAccessEndDate(props.product) === null) {
				endDate = productInfo.getLongestEndDate(props.product);
			} else {
				endDate = new Date(
					Math.max(
						productInfo.getLongestEndDate(props.product).getTime(),
						productInfo.getEmergencyAccessEndDate(props.product)!.getTime()
					)
				);
			}
			let messageText =
				t(texts.systemCard.errorProductAboutToExpire) +
				endDate.toDateString() +
				'.';
			let severity: MessageSeverity = 'info';
			let icon: IconName | undefined = 'warning';
			if (
				!productInfo.isAnyLicenseLongLasting(
					props.product,
					config.EMERGENCY_EDIT_DAYS
				)
			) {
				messageText =
					t(texts.systemCard.errorProductAboutToExpireShortly) +
					endDate.toDateString() +
					'.';
				severity = 'warning';
				icon = undefined;
			}
			setMessage(() => ({
				severity: severity,
				icon: icon,
				text: messageText,
			}));
		}
	}, [props.product]);

	const messageAreaContent = message && (
		<div className={styles.messageArea}>
			<MessageArea className={styles.messageArea} message={message} />
		</div>
	);

	const latestClipboardBatch =
		props.clipboardBatches[props.clipboardBatches.length - 1] ?? [];
	const clipboardContent = (
		<div
			className={styles.clipboardPopover}
			onMouseEnter={() => setHoveringClipboardMenu(true)}
			onMouseLeave={() => setHoveringClipboardMenu(false)}
		>
			<h4 className={styles.clipboardTitle}>
				{t(texts.systemLandscapeHeader.viewClipboard)}
			</h4>
			{latestClipboardBatch.length === 0 ? (
				<div className={styles.clipboardEmptyText}>
					{t(texts.systemLandscapeHeader.clipboardEmpty)}
				</div>
			) : (
				<div className={styles.clipboardList}>
					<div className={styles.clipboardBatch}>
						<h5 className={styles.clipboardBatchTitle}>
							{t(texts.systemLandscapeHeader.clipboardBatchLatest, {
								count: latestClipboardBatch.length,
							})}
						</h5>
						{latestClipboardBatch.map((system, indexInBatch) => (
							<div
								className={styles.clipboardListItem}
								key={`${props.clipboardBatches.length - 1}-${indexInBatch}-${system.SYSTEM_ID}-${system.SYSTEM_CLIENT}-${system.INSTNUMBER}`}
							>
								<div className={styles.clipboardListHeader}>
									{t(texts.systemLandscapeHeader.clipboardSystemLabel, {
										index: indexInBatch + 1,
									})}
								</div>
								<div><b>{t(texts.general.system.systemId)}:</b> {system.SYSTEM_ID || '-'}</div>
								<div><b>{t(texts.general.system.systemClient)}:</b> {system.SYSTEM_CLIENT || '-'}</div>
								<div><b>{t(texts.general.system.installNumber)}:</b> {system.INSTNUMBER || '-'}</div>
								<div><b>{t(texts.general.description)}:</b> {system.DESCRIPTION || '-'}</div>
								<div><b>{t(texts.systemCard.systemRoles)}:</b> {system.SYSTEM_ROLES?.length > 0 ? system.SYSTEM_ROLES.join(', ') : '-'}</div>
								<div><b>{t(texts.systemCard.systemRequirements)}:</b> {system.SYSTEM_REQMTS?.length > 0 ? system.SYSTEM_REQMTS.join(', ') : '-'}</div>
								<div><b>{t(texts.general.totalSystems)}:</b> {system.LIC_TOT_COUNT ?? system.MAX_LIC_TOT_COUNT ?? '-'}</div>
								<div><b>{t(texts.general.productionSystems)}:</b> {system.LIC_PRD_COUNT ?? system.MAX_LIC_PRD_COUNT ?? '-'}</div>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);

	const licenseDates = (
		<div className={styles.header}>
			<div className={styles.headerElement}>
				<b>{t(texts.general.productBeginning)}</b>
				{formatDate(productInfo.getShortestStartDate(props.product).toString())}
			</div>
			<div className={styles.headerElement}>
				<b>{t(texts.general.productExpiration)}</b>
				<div
					style={{
						color: productInfo.isExpired(props.product)
							? Color.DANGER
							: productInfo.isAboutToExpire(props.product)
							? Color.WARNING
							: 'black',
					}}
				>
					{formatDate(productInfo.getLongestEndDate(props.product).toString())}
				</div>
			</div>
			{/* Code Changes for System, Landscapes and Languages Count */}
			{(props.product.MAX_LIC_TOT_COUNT ?? 0) > 0 && (
				<div className={styles.headerElement}>
					<b>{t(texts.general.totalSystems)}</b>
					<div
						style={{
							color: 'black',
						}}
					>
						{props.product.MAX_LIC_TOT_COUNT ?? props.totalProductSystemsCount ?? 0}
					</div>
				</div>
			)}
			{(props.product.MAX_LIC_PRD_COUNT ?? 0) > 0 && (
				<div className={styles.headerElement}>
					<b>{t(texts.general.productionSystems)}</b>
					<div
						style={{
							color: 'black',
						}}
					>
						{props.product.MAX_LIC_PRD_COUNT ?? props.productionSystemsCount ?? 0}
					</div>
				</div>
			)}
			<div className={styles.headerElement}>
				<b>{t(texts.general.systems)}</b>
				<div
					style={{
						color: 'black',
					}}
				>
					{props.totalSystemsCount ?? props.systems?.length ?? 0}
				</div>
			</div>
			{(props.totalLandscapesCount ?? 0) > 0 && (
				<div className={styles.headerElement}>
					<b>{t(texts.general.landscapes)}</b>
					<div
						style={{
							color: 'black',
						}}
					>
						{props.totalLandscapesCount ?? 0}
					</div>
				</div>
			)}
			{(props.LANGUAGE_COUNT ?? 0) > 0 && (
				<div className={styles.headerElement}>
					<b>{t(texts.general.languages)}</b>
					<div
						style={{
							color: 'black',
						}}
					>
						{props.LANGUAGE_COUNT}
					</div>
				</div>
			)}
			{productInfo.getEmergencyAccessEndDate(props.product) &&
				productInfo.getEmergencyAccessEndDate(props.product)! >
					productInfo.getLongestEndDate(props.product) && (
					<div className={styles.headerElement}>
						<b>{t(texts.general.emergyncyLicenseExpiration)}</b>
						<div
							style={{
								color: productInfo.isEmergencyExpired(props.product)
									? Color.DANGER
									: productInfo.isEmergencyAboutToExpire(props.product)
									? Color.WARNING
									: 'black',
							}}
						>
							{formatDate(
								productInfo.getEmergencyAccessEndDate(props.product)!.toString()
							)}
						</div>
					</div>
				)}
				<div className={styles.copyPasteControls}>
				<div className={styles.headerElementSwitch}>
					<b>{t(texts.systemLandscapeHeader.enableSystemsCopyPaste)}</b>
					<Switch
						checked={props.enableSystemLandscapeCopyPaste}
						onChange={(event) =>
							props.onEnableSystemLandscapeCopyPaste(event.target.checked)
						}
					/>
				</div>
				{props.enableSystemLandscapeCopyPaste && (
					<>
						<Button
							icon='copy'
							intent='none'
							text={`${t(texts.systemLandscapeHeader.copySystems)} (${props.selectedSystemsCount})`}
							onClick={() => void props.onCopySelectedSystems()}
						/>
						<Popover
							content={clipboardContent}
							placement='bottom-end'
							isOpen={isClipboardPopoverOpen}
							onClose={() => {
								setClipboardPopoverOpen(false);
								setHoveringClipboardButton(false);
								setHoveringClipboardMenu(false);
							}}
						>
							<Button
								icon='eye'
								intent='primary'
								text={`${t(texts.systemLandscapeHeader.viewClipboard)} (${latestClipboardBatch.length})`}
								onMouseEnter={() => setHoveringClipboardButton(true)}
								onMouseLeave={() => setHoveringClipboardButton(false)}
							/>
						</Popover>
					</>
				)}
			</div>
		</div>
	);

	return (
		// Code Changes for Expand & Collapse of System Landscapes
		<>
			<div>
				<Section
					rightElement={
						props.systemsExpandible ? (
							<Button
								onClick={(e) => {
									e.stopPropagation();
									props.onCollapseSystems();
								}}
								icon={'caret-up'}
								text={t(texts.systemLandscapeHeader.captionCollapseSystems)}
								intent='none'
							/>
						) : (
							<Button
								onClick={(e) => {
									e.stopPropagation();
									props.onExpandSystems();
								}}
								icon={'caret-down'}
								text={t(texts.systemLandscapeHeader.captionExpandSystems)}
								intent='none'
							/>
						)
					}
					collapsible={true}
					title={t(texts.systemLandscapeHeader.licenseOptions)}
					icon='file-seal'
				>
					<SectionCard>
						<div className={styles.messageAreaContainer}>
							{licenseDates}
							{messageAreaContent}
						</div>
					</SectionCard>
					<SectionCard padded={false}>
						<Card compact>
							<div>
								{!collapseLicenseOptions && (
									<>
										{!collapseLicenseOptions && (
											<div className={styles.licenseOptions}>
												{props.licenseOptions.length === 0 &&
												props.isLicenseOptionsRequired === true ? (
													<>
														{t(texts.systemLandscapeHeader.noLicenseOptions, {
															supportPortal: (
																<Link to={config.SUPPORT_PORTAL_LINK}>
																	{config.SUPPORT_PORTAL_LINK}
																</Link>
															),
														})}
													</>
												) : (
													<CompoundTags
														objects={props.licenseOptions}
														getName={(licenseOption) =>
															licenseOption.DESCRIPTION
														}
														getValue={(licenseOption) =>
															licenseOption.LICENSE_OPTION_VALUE
														}
													/>
												)}
											</div>
										)}
									</>
								)}
							</div>
							<div className={styles.buttonGroup}>
								<ButtonGroup vertical>
									{/* {props.systemsExpandible ? (
                    <Button
                      onClick={props.onExpandSystems}
                      icon={"caret-down"}
                      text={t(texts.systemLandscapeHeader.captionExpandSystems)}
                      intent="none"
                    />
                  ) : (
                    <Button
                      onClick={props.onCollapseSystems}
                      icon={"caret-up"}
                      text={t(
                        texts.systemLandscapeHeader.captionCollapseSystems
                      )}
                      intent="none"
                    />
                  )} */}
									{useAuth().isSuperUser() && !collapseLicenseOptions && (
										<div>
											{toggleAllLicensesCommand.view}
											<Button
												icon={<Icon icon='lifebuoy' intent='danger' />}
												text={t(texts.systemLandscape.captionSetAllLicense, {
													action: allLicensesToggle
														? t(texts.systemLandscape.actionRevoke)
														: t(texts.systemLandscape.actionGrant),
												})}
												intent={allLicensesToggle ? 'warning' : 'none'}
												onClick={() => toggleAllLicensesCommand.execute()}
											/>
										</div>
									)}
								</ButtonGroup>
							</div>
						</Card>
					</SectionCard>
					{/* { Empty div creates a bottom-separator for the header } */}
					<div></div>
				</Section>
			</div>
		</>
	);
};
